import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useParams, useNavigate } from 'react-router-dom';

import { Send } from '@mui/icons-material';

import CommonButton from '../../buttons/common-button';
import FoldersService from '../../../services/FoldersService';
import AppointmentsService from '../../../services/AppointmentsService';
import SendFolderModal from './modals/modal--folder--send-folder';
import InfoBox from '../../blocks/messages/info-box';
import CustomModal from '../../blocks/custom-modal';

/**
 * OverviewTools Component
 * 
 * This component provides action buttons to interact with a report.
 */
const OverviewTools = () => {
    const { refFolder } = useParams(); // Get the folder number from the URL

    const navigate = useNavigate(); // Hook for navigation

    const [folder, setFolder] = useState(null); // State to store the folder details

    // State to handle loading and error messages
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [succes, setSucces] = useState(null);

    const [unlockedAppointment, setUnlockedAppointment] = useState(false);

    const [modal, setModal] = useState(false);
    const [warningModal, setWarningModal] = useState(false);

    /**
     * Fetches the folder data from the server.
     * This function fetches the folder data based on the folder number.
     * If the folder is not found, an error is thrown.
     */
    const fetchData = async () => {
        try {
            // Set loading state to true
            setLoading(true);

            // Fetch the folder based on the folder number
            const folder = await FoldersService.getFolderByNumber(refFolder);

            // If the folder is not found, throw an error
            if (!folder) {
                setError(new Error('Dossier introuvable'));
                return;
            }

            setFolder(folder);

            const appointmentsData = await AppointmentsService.getAppointmentsByFolderId(folder.id);
            
            // If the folder has no appointments, set the appointments state to an empty array
            if (!appointmentsData || appointmentsData.length === 0) {
                setUnlockedAppointment(false);
                return;
            }

            // Find the last unlocked appointment
            const lastUnlockedAppointment = appointmentsData.find(appointment => !appointment.is_locked);
            if (lastUnlockedAppointment) {
                setUnlockedAppointment(true);
            }
        } catch (error) {
            setError(error?.message || "Une erreur inattendue est survenue");
        } finally {
            setLoading(false);
        }
    }

    // Fetch the data when the component mounts
    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Handles sending the report by e-mail.
     * This function triggers the logic to send the report by e-mail.
     */
    const handleSendEmail = () => {
        setModal(true);
    };

    const checkFolder = () => {
        // If the folder is not found, display an error message
        if (!folder) {
            setError('Dossier introuvable');
            return;
        }

        // If the folder is locked, display an error message
        if (unlockedAppointment) {
            setWarningModal(true);
            return;
        }

        // If the folder is found and not locked, open the modal to send the folder
        handleSendEmail();
    };

    // Display a success message when the report is sent by e-mail
    useEffect(() => {
        if (succes) {
            setTimeout(() => {
                setSucces(null);
            }, 5000);
        }
    }, [succes]);

    // Return null if the folder data is loading or not available
    if (loading || !folder || error) return;

    // Render the overview tools component
    return (
        <React.Fragment>

            { /* Display error message */}
            { succes && (<InfoBox type="validation" text={succes} />)}

            { /* Display overview tools */}
            <div className="overview-tools">

                { /* Action buttons */}
                <div className="action-buttons">
                    <CommonButton
                        onClick={checkFolder}
                        label="Confirmer et transmettre le dossier"
                        icon={<Send />}
                        className="send-email"
                    />
                </div>
            </div>

            { /* Modal to display warning when folder is locked */}
            { warningModal && (
                <CustomModal
                    open={warningModal}
                    onClose={() => setWarningModal(false)}
                    title="Attention signature(s) manquante(s)"
                    handleSave={() => {
                        setWarningModal(false);
                        navigate(`/folders/${refFolder}/payments-and-signature`);
                    }}
                    handleSaveLabel="Aller à la signature"
                    onCloseLabel="Fermer"
                >
                    <InfoBox
                        type="warning"
                        text={
                            <React.Fragment>
                                <p>Le dossier est actuellement en cours de traitement.</p>
                                <p>Veuillez signer le rapport d'intervention avant de pouvoir envoyer le dossier.</p>
                            </React.Fragment>
                        }
                    />
                </CustomModal>
            )}

            { /* Modal to send the folder */}
            { !warningModal && modal && (
                <SendFolderModal
                    open={modal}
                    onClose={() => setModal(false)}
                    folder={folder}
                    onSend={() => setSucces('Le dossier a été envoyé par e-mail avec succès')}
                />
            )}
        </React.Fragment>
    );
};

// Prop types for OverviewTools component
OverviewTools.propTypes = {
    overviewRef: PropTypes.object.isRequired,
};

export default OverviewTools;